import { Link } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, MapPin } from "lucide-react";
import { useMiniSite } from "../../components/minisite/context";
import TrustBadges from "../../components/TrustBadges";
import {
  MiniSiteHero, MiniSiteHowItWorks, PublicVehicleCard,
} from "../RentalProfile";

/** Enterprise mini-site: Home page.
 *  Hero, a teaser of featured vehicles, how-it-works, and short reviews
 *  preview. Detailed sections (Policies/FAQ/Reviews) live on their own
 *  pages.
 */
export default function MiniSiteHome() {
  const { data, slug, theme } = useMiniSite();
  const ms = data.mini_site || {};
  const sections = ms.sections_enabled || {};
  const featuredIds = ms.featured_vehicle_ids || [];
  const vehicles = data.vehicles || [];
  const featured = featuredIds.length
    ? vehicles.filter((v) => featuredIds.includes(v.id))
    : vehicles.slice(0, 3);
  const previewReviews = (data.reviews || []).slice(0, 2);

  return (
    <div data-testid="ms-home-page">
      <MiniSiteHero
        company={data}
        ms={ms}
        theme={theme}
        logoUrl={data.public_logo_url}
        enabled={sections.hero !== false}
      />

      {/* Quick context strip */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid sm:grid-cols-3 gap-3">
        <InfoCard label="Service area" value={data.city || ms.service_area_text || "Philippines"} icon={<MapPin className="w-4 h-4" />} theme={theme} />
        <InfoCard label="Rental type" value={(data.service_type || data.business_type || "rental").replace(/_/g, " ")} icon={<ShieldCheck className="w-4 h-4" />} theme={theme} />
        {data.rating_avg != null ? (
          <InfoCard label={`${data.rating_count} booking-linked review${data.rating_count === 1 ? "" : "s"}`} value={`${data.rating_avg.toFixed(1)} ★`} icon={<Star className="w-4 h-4" />} theme={theme} />
        ) : (
          <InfoCard label="ConfirmPH" value="Powered booking confirmations" icon={<ShieldCheck className="w-4 h-4" />} theme={theme} />
        )}
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-2">
        <TrustBadges companyId={data.id} />
      </div>

      {/* Featured vehicles */}
      {featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2" data-testid="ms-home-featured">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
            <h2 className="font-display text-2xl font-semibold" style={{ color: theme }}>
              Featured rentals
            </h2>
            <Link
              to={`/rentals/${slug}/fleet`}
              className="text-sm font-medium hover:underline inline-flex items-center gap-1"
              style={{ color: theme }}
              data-testid="ms-home-view-all-fleet"
            >
              View full fleet <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((v) => (
              <PublicVehicleCard key={v.id} companyId={data.id} vehicle={v} />
            ))}
          </div>
        </section>
      )}

      <MiniSiteHowItWorks theme={theme} enabled={sections.how_it_works !== false} />

      {/* Reviews preview */}
      {previewReviews.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="ms-home-reviews-preview">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
            <h2 className="font-display text-2xl font-semibold" style={{ color: theme }}>
              What renters say
            </h2>
            <Link
              to={`/rentals/${slug}/reviews`}
              className="text-sm font-medium hover:underline inline-flex items-center gap-1"
              style={{ color: theme }}
            >
              All reviews <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {previewReviews.map((r) => (
              <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-1 text-amber-600 text-sm font-semibold">
                  {Array.from({ length: r.rating || 5 }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-amber-500 stroke-amber-500" />
                  ))}
                </div>
                <p className="text-sm text-slate-700 mt-1 line-clamp-3">{r.comment || r.review}</p>
                <div className="text-xs text-slate-500 mt-2">- {r.customer_name || "ConfirmPH renter"}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-2xl p-6 sm:p-8 text-white" style={{ background: theme }}>
          <h3 className="font-display text-2xl sm:text-3xl font-semibold">Ready to ride?</h3>
          <p className="mt-2 text-white/90 max-w-2xl">
            {ms.intro || "Browse the fleet, send a booking request, and get a clear confirmation through ConfirmPH."}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to={`/rentals/${slug}/booking`}
              data-testid="ms-home-cta-book"
              className="px-5 py-2.5 rounded-lg bg-white text-sm font-semibold inline-flex items-center gap-1 hover:bg-slate-100"
              style={{ color: theme }}
            >
              {!data.online_booking_enabled ? "Send inquiry"
                : ms.main_cta_type === "send_inquiry" ? "Send inquiry"
                : ms.main_cta_type === "book_now" ? "Book now"
                : "Request booking"}
            </Link>
            <Link
              to={`/rentals/${slug}/contact`}
              className="px-5 py-2.5 rounded-lg border border-white/40 text-white/95 hover:bg-white/10 text-sm font-medium"
            >
              Contact business
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ label, value, icon, theme }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-start gap-2.5">
      <span className="w-8 h-8 rounded grid place-items-center text-white flex-shrink-0" style={{ background: theme }}>
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className="text-sm font-medium text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}
